import axios from 'axios';
import { useCallback, useState } from 'react';
import {
  SubscriptionActionService,
  type CancelPauseRequest,
  type ChangeDispatchDateRequest,
  type SubscriptionAction,
} from '../services/subscriptionActions';

export const useSubscriptionActions = (onSuccess?: () => void) => {
  const [loadingActions, setLoadingActions] = useState<Record<number, boolean>>({});
  const [error, setError] = useState<string | null>(null);

  const executeAction = useCallback(
    async (
      shopifyCustomerId: number,
      action: SubscriptionAction,
      params?: CancelPauseRequest | ChangeDispatchDateRequest,
    ): Promise<boolean> => {
      setLoadingActions((prev) => ({ ...prev, [shopifyCustomerId]: true }));
      setError(null);

      try {
        switch (action) {
          case 'cancel':
            await SubscriptionActionService.cancel(shopifyCustomerId, params as CancelPauseRequest);
            break;
          case 'pause':
            await SubscriptionActionService.pause(shopifyCustomerId, params as CancelPauseRequest);
            break;
          case 'reactivate':
            await SubscriptionActionService.reactivate(shopifyCustomerId);
            break;
          case 'shipNow':
            await SubscriptionActionService.shipNow(shopifyCustomerId);
            break;
          case 'changeDispatchDate':
            await SubscriptionActionService.changeDispatchDate(
              shopifyCustomerId,
              params as ChangeDispatchDateRequest,
            );
            break;
        }

        onSuccess?.();
        return true;
      } catch (err: unknown) {
        const response = axios.isAxiosError<{
          errors?: { nextDispatchDate?: string | string[] };
          message?: string;
        }>(err)
          ? err.response
          : undefined;
        // Handle 422 validation errors specifically for date changes
        if (action === 'changeDispatchDate' && response?.status === 422) {
          const errorData = response.data;

          // Extract validation message for nextDispatchDate field
          if (errorData?.errors?.nextDispatchDate) {
            const messages = Array.isArray(errorData.errors.nextDispatchDate)
              ? errorData.errors.nextDispatchDate
              : [errorData.errors.nextDispatchDate];
            setError(messages[0]);
          } else if (errorData?.message) {
            setError(errorData.message);
          } else {
            setError(
              'Invalid date selected. Please choose a date between tomorrow and 6 months from now.',
            );
          }
        } else if (err instanceof Error) {
          setError(err.message);
        } else if (
          response?.data &&
          typeof response.data === 'object' &&
          'message' in response.data &&
          typeof response.data.message === 'string'
        ) {
          setError(response.data.message);
        } else {
          setError('Action failed');
        }
        return false;
      } finally {
        setLoadingActions((prev) => ({ ...prev, [shopifyCustomerId]: false }));
      }
    },
    [onSuccess],
  );

  const isLoading = useCallback(
    (shopifyCustomerId: number) => {
      return loadingActions[shopifyCustomerId] || false;
    },
    [loadingActions],
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    executeAction,
    isLoading,
    error,
    clearError,
  };
};
